import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  getLandingArchiveIndex2ContentWidth,
  type LandingArchiveCandidate,
} from '../shared/util/landingArchiveIndex';
import { useDeviceStore } from '../shared/store/deviceStore';
import { DeviceType } from '../shared/enum/EnumRepository';
import { useExhibitionVenue } from '../shared/hooks/useApi/useExhibitionVenue';
import {
  buildExhibitionVenueIndexColumns,
  buildExhibitionVenueIndexGroups,
  EXHIBITION_VENUE_LAYOUT_GAP,
  EXHIBITION_VENUE_VIEWER_MAX_WIDTH,
  EXHIBITION_VENUE_VIEWER_MIN_WIDTH,
  getExhibitionVenueIndexMaxColumns,
} from '../shared/util/exhibitionVenueIndex';
import ExhibitionMetaStation from '../component/exhibitions/ExhibitionMetaStation';
import ExhibitionRightBottomIsland from '../component/exhibitions/ExhibitionRightBottomIsland';
import ExhibitionVenueIndex from '../component/exhibitions/ExhibitionVenueIndex';
import ExhibitionVenueViewer from '../component/exhibitions/ExhibitionVenueViewer';
import ExhibitionMetaStationMobile from '../component/exhibitions/mobile/ExhibitionMetaStationMobile';
import ExhibitionVenueIndexMobile from '../component/exhibitions/mobile/ExhibitionVenueIndexMobile';
import ExhibitionVenueViewerMobile from '../component/exhibitions/mobile/ExhibitionVenueViewerMobile';
import { getPageMessage } from '../shared/constants/pageMessages';
import { useLanguageStore } from '../shared/store/languageStore';

const Exhibitions: React.FC = () => {
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const language = useLanguageStore((s) => s.language);
  const venueAreaRef = useRef<HTMLDivElement>(null);
  const [venueAreaWidth, setVenueAreaWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const {
    venueList,
    selectedVenueId,
    loadVenues,
    selectVenue,
    clearActiveVenue,
  } = useExhibitionVenue();

  useEffect(() => {
    void loadVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const venueArea = venueAreaRef.current;
    if (!venueArea) return;

    const measure = () => {
      setVenueAreaWidth(venueArea.clientWidth);
      setViewportWidth(window.innerWidth);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(venueArea);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isMobile]);

  const desktopMaxColumns = useMemo(
    () => getExhibitionVenueIndexMaxColumns(
      venueAreaWidth,
      viewportWidth,
      Boolean(selectedVenueId),
    ),
    [venueAreaWidth, viewportWidth, selectedVenueId],
  );

  const indexColumns = useMemo(
    () => buildExhibitionVenueIndexColumns(venueList, {
      language,
      maxColumns: desktopMaxColumns,
    }),
    [venueList, language, desktopMaxColumns],
  );

  const indexGroups = useMemo(
    () => buildExhibitionVenueIndexGroups(venueList, language),
    [venueList, language],
  );

  const handleVenueActivate = useCallback(
    (candidate: LandingArchiveCandidate) => {
      const venueId = candidate.aui?.trim();
      if (!venueId) return;
      if (selectedVenueId === venueId) {
        clearActiveVenue();
        return;
      }
      selectVenue(venueId);
    },
    [selectedVenueId, clearActiveVenue, selectVenue],
  );

  const handleBackgroundClick = () => {
    if (selectedVenueId) clearActiveVenue();
  };

  if (isMobile) {
    return (
      <ExhibitionsMobileContainer onClick={handleBackgroundClick}>
        <ExhibitionRightBottomIsland />
        <ExhibitionMetaStationMobile />
        {selectedVenueId ? (
          <ExhibitionVenueViewerMobile />
        ) : indexGroups.length === 0 ? (
          <StatusMessage>{getPageMessage('EXHIBITION_NO_MATCHING_RESULTS')}</StatusMessage>
        ) : (
          <MobileIndexArea>
            <ExhibitionVenueIndexMobile
              groups={indexGroups}
              activeVenueId={selectedVenueId}
              onVenueActivate={handleVenueActivate}
            />
          </MobileIndexArea>
        )}
      </ExhibitionsMobileContainer>
    );
  }

  return (
    <ExhibitionsContainer onClick={handleBackgroundClick}>
      <ExhibitionRightBottomIsland />
      <ContentRow>
        <LeftColumn>
          <ExhibitionMetaStation />
        </LeftColumn>
        <VenueArea ref={venueAreaRef}>
          <IndexArea $columnCount={Math.max(1, indexColumns.length)}>
            {indexColumns.length === 0 ? (
              <StatusMessage>{getPageMessage('EXHIBITION_NO_MATCHING_RESULTS')}</StatusMessage>
            ) : (
              <ExhibitionVenueIndex
                columns={indexColumns}
                activeVenueId={selectedVenueId}
                onVenueActivate={handleVenueActivate}
              />
            )}
          </IndexArea>
          {selectedVenueId && (
            <ViewerArea onClick={(e) => e.stopPropagation()}>
              <ExhibitionVenueViewer />
            </ViewerArea>
          )}
        </VenueArea>
      </ContentRow>
    </ExhibitionsContainer>
  );
};

/** Header/floating island clearance. Index uses this as scrollable padding so content can reach viewport top. */
const EXHIBITIONS_TOP_OFFSET = 'calc(clamp(100px, 12vh, 140px) + clamp(12px, 2vh, 24px))';

const ExhibitionsContainer = styled.main`
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const ExhibitionsMobileContainer = styled.main`
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: clamp(80px, 20vh, 90px) clamp(14px, 4vw, 20px) clamp(24px, 4vh, 48px);
`;

const ContentRow = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  overflow: hidden;
  padding: 0 clamp(20px, 4vw, 30px) clamp(24px, 4vh, 48px);
  box-sizing: border-box;
  gap: 10px;
`;

const LeftColumn = styled.div`
  flex-shrink: 0;
  height: 100%;
  min-height: 0;
  padding-top: ${EXHIBITIONS_TOP_OFFSET};
  box-sizing: border-box;
`;

const VenueArea = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: 0;
  display: flex;
  gap: ${EXHIBITION_VENUE_LAYOUT_GAP}px;
  overflow: hidden;
`;

const IndexArea = styled.div<{ $columnCount: number }>`
  flex: 1 0 ${({ $columnCount }) =>
    getLandingArchiveIndex2ContentWidth($columnCount, false)};
  min-width: ${({ $columnCount }) =>
    getLandingArchiveIndex2ContentWidth($columnCount, false)};
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: ${EXHIBITIONS_TOP_OFFSET};
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MobileIndexArea = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const ViewerArea = styled.div`
  flex: 0 1 ${EXHIBITION_VENUE_VIEWER_MAX_WIDTH}px;
  width: clamp(
    ${EXHIBITION_VENUE_VIEWER_MIN_WIDTH}px,
    29vw,
    ${EXHIBITION_VENUE_VIEWER_MAX_WIDTH}px
  );
  min-width: ${EXHIBITION_VENUE_VIEWER_MIN_WIDTH}px;
  max-width: ${EXHIBITION_VENUE_VIEWER_MAX_WIDTH}px;
  height: 100%;
  min-height: 0;
  padding-top: ${EXHIBITIONS_TOP_OFFSET};
  box-sizing: border-box;
`;

const StatusMessage = styled.div`
  width: 100%;
  padding-top: 3px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

export default Exhibitions;
